<template>
	<v-dialog
		v-model="localShowDialog"
		scrollable
		@click:outside="closeDialog"
		@keydown.esc="closeDialog"
		:width="$vuetify.display.mobile ? '95%' : '500px'"
	>
		<v-card
			class="sgnl-pb-4"
			data-test="build-directory-selector-dialog"
		>
			<v-card-title class="dialog-header sgnl-subtitle-medium">
				Select Build Directory
				<v-spacer />
				<v-btn
					icon
					variant="text"
					size="small"
					@click="closeDialog"
				>
					<Close16 />
				</v-btn>
			</v-card-title>
			<v-divider />
			<v-card-text class="dialog-body build-directory-body">
				<div
					v-if="loading"
					class="sgnl-flex sgnl-justify-center"
				>
					<v-progress-circular
						color="primary"
						size="40"
					/>
				</div>
				<!-- v-model:active="highlightedWorkingDirectory"
		 -->
				<v-treeview
					v-else
					v-model:activated="selectedItems"
					v-model:opened="openItems"
					:items="repositoryDirectories"
					item-value="path"
					item-title="name"
					activatable
					transition
					density="compact"
					class="sgnl-paragraph-s-regular"
				>
					<template v-slot:prepend="{ isOpen }">
						<FolderOpen16 v-if="isOpen" />
						<Folder16 v-else />
					</template>
				</v-treeview>
				<AlertMessage
					:show-alert="showError"
					type="error"
				>
					{{ errorMessage }}
				</AlertMessage>
			</v-card-text>
			<v-divider />
			<v-card-actions class="dialog-footer">
				<v-btn
					variant="outlined"
					@click="closeDialog"
					>Cancel</v-btn
				>
				<v-btn
					variant="flat"
					color="primary"
					@click="setBuildDirectoryHandler"
				>
					Set Directory
				</v-btn>
			</v-card-actions>
		</v-card>
	</v-dialog>
</template>

<script lang="ts">
import { useGithubStore } from "@/stores/github";
import { defineComponent } from "vue";
import Close16 from "@carbon/icons-vue/es/close/16";
import Folder16 from "@carbon/icons-vue/es/folder/16";
import FolderOpen16 from "@carbon/icons-vue/es/folder--open/16";

import { getRepositoryTreeRecursive } from "@/js/githubClient";
import { githubTreeToFileTree, generateGithubResponseErrorMessage } from "@/js/githubUtilities";
import { FileTree } from "@/types/general";
import { monitoringCaptureError } from "@/plugins/monitoring";

import AlertMessage from "@/components/Common/AlertMessage.vue";
import axios from "axios";

type ComponentData = {
	repositoryDirectories: FileTree[];
	selectedItems: string[];
	openItems: string[];
	showError: boolean;
	errorMessage: string;
	githubLoggedIn: boolean;
	loading: boolean;
};

const defaultWorkingDirectory = { name: "./", path: "", type: "dir", children: [] };

export default defineComponent({
	name: "RepositoryBuildDirectoryDialog",
	components: { AlertMessage, Close16, Folder16, FolderOpen16 },
	props: {
		showDialog: { type: Boolean, default: false },
		repositoryFullName: { type: String, default: undefined },
		shaName: { type: String, default: undefined },
		selectedDirectoryPath: { type: String, default: undefined },
	},
	emits: {
		"update:showDialog": (_show: boolean) => true,
		"update:selectedDirectoryPath": (_dir: string) => true,
	},
	data: (): ComponentData => ({
		repositoryDirectories: [defaultWorkingDirectory],
		selectedItems: [defaultWorkingDirectory.path],
		openItems: [defaultWorkingDirectory.path],
		showError: false,
		errorMessage: "",
		githubLoggedIn: false,
		loading: true,
	}),
	setup() {
		const githubStore = useGithubStore();
		return {
			githubStore,
		};
	},
	computed: {
		localShowDialog: {
			get() {
				return this.showDialog;
			},
			set(value) {
				this.$emit("update:showDialog", value);
			},
		},
	},
	methods: {
		closeDialog() {
			this.$emit("update:showDialog", false);
		},
		setBuildDirectoryHandler() {
			if (this.selectedItems.length > 0) {
				this.$emit("update:selectedDirectoryPath", this.selectedItems[0]);
			}
			this.closeDialog();
		},
	},
	async created() {
		if (this.repositoryFullName != undefined && this.shaName != undefined) {
			try {
				const response = await getRepositoryTreeRecursive(this.repositoryFullName, this.shaName);
				const fileTree = githubTreeToFileTree(response.data);
				this.repositoryDirectories = [fileTree];
			} catch (error) {
				if (axios.isAxiosError(error)) {
					this.errorMessage = generateGithubResponseErrorMessage(error, this.githubLoggedIn);

					/*
					 *	Override generateGithubResponseErrorMessage return message to give component specific error message.
					 */
					if (error.response) {
						/*
						 *	According to https://docs.github.com/en/rest/git/trees?apiVersion=2022-11-28#get-a-tree--status-codes
						 *	this endpoint can only fail with 404 and 422.
						 */
						switch (error.response.status) {
							case 404: {
								this.errorMessage =
									`GitHub error: ${this.repositoryFullName}@${this.shaName} not found.` +
									" If you think this is a mistake, please contact support at developer-support@signaloid.com.";
								break;
							}
							case 422: {
								this.errorMessage =
									`GitHub error: Validation failed, or the endpoint has been spammed.` +
									" If this error persists, please contact support at developer-support@signaloid.com.";
								break;
							}
							/*
							 *	Other cases handled in generateGithubResponseErrorMessage above.
							 */
						}
					}
				} else {
					this.errorMessage = `The Signaloid Cloud Developer Platform encountered an error while accessing the repository. 
					Please try again. If this error persists, please contact support at developer-support@signaloid.com.`;
				}

				this.showError = true;

				const privacyFriendlyErrorMessage = this.errorMessage.replaceAll(
					this.repositoryFullName,
					"<repository-name-redacted>",
				);
				monitoringCaptureError(error, "getRepositoryTreeRecursive", {
					extras: {
						userMessage: privacyFriendlyErrorMessage,
					},
				});

				/*
				 *	Nothing much to do if we don't have the directories
				 */
				return;
			}
		}

		/*
		 *	Breadth-first search for correct item. We could just do:
		 *
		 *	    ```js
		 *	    this.selectedItems = [this.selectedDirectoryPath];
		 *	    ```
		 *
		 *	but the problem is this component does not know if the passed path is valid yet. The
		 *	getRepositoryTreeRecursive() is made right above so the parent may have an invalid path.
		 */
		const queue = [this.repositoryDirectories[0]];
		let found = false;
		while (queue.length > 0) {
			const item = queue.shift()!; // We just checked the length
			if (item.path === this.selectedDirectoryPath) {
				this.selectedItems = [item.path];
				found = true;
				break;
			}
			queue.push(...(item.children ?? []));
		}
		if (!found) {
			/*
			 *	The parent has passed a selectedDirectoryPath that we could not find in the full tree of the given
			 *	repository and sha/branch. The parent needs to update its selection.
			 */
			this.selectedItems = [this.repositoryDirectories[0].path];
			this.$emit("update:selectedDirectoryPath", this.repositoryDirectories[0].path);
		}

		/*
		 *
		 */

		this.loading = false;
	},
});
</script>

<style lang="scss" scoped>
.build-directory-body {
	max-height: 500px;
	max-width: 600px;
}
</style>
